/**
 * @fileOverview The main entry point to your standalone. Start editing here.
 */

import React, { useEffect, useState } from 'react'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/reset.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/site.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/segment.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/container.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/header.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/image.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/icon.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/list.css'
import './styles.scss'
import { csvParse } from 'd3-dsv'
import { CountryRow } from 'components/CountryRow'
import { ThemeSwitch } from '@ta-interaktiv/react-theme-switch'
import { useWindowSize } from 'hooks/useWindowSize'

const LOCALES_DE = require('./locales_DE.json') as LOCALE_TYPE
const LOCALES_FR = require('./locales_FR.json') as LOCALE_TYPE
const FIXED_UPDATE_DATE = '2021-12-07'
const params = new URL(window.location.toString())

const LOCALES =
  params.searchParams.get('lang') === 'FR' ? LOCALES_FR : LOCALES_DE
const countriesToRemove = [
  'EU',
  'HIC',
  'UMC',
  'LMC',
  'LIC',
  'Nordamerika',
  'Oceanien',
  'International',
  'Asien',
  'Afrika',
  'Europa',
  'Welt',
  'Südamerika'
]
const maxperstart = 20

const formatDate = (a: string) =>
  new Date(a).toLocaleDateString(LOCALES.batch.localeFormat, {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric'
  })

interface CountryData {
  change: string
  code: string
  name: string
  perPop: number
  quarantine: boolean
  qstart?: string
  qend?: string
  sum14: number
  sum14Before: number
}
const sorts: { [key: string]: (a: CountryData, b: CountryData) => number } = {
  perPop: (a: CountryData, b: CountryData) => (b.perPop > a.perPop ? 1 : -1),
  perPop_reverse: (a: CountryData, b: CountryData) =>
    b.perPop > a.perPop ? -1 : 1,
  quar: (a: CountryData, b: CountryData) =>
    a.quarantine === b.quarantine ? 0 : a.quarantine ? -1 : 1,
  quar_reverse: (a: CountryData, b: CountryData) =>
    b.quarantine === a.quarantine ? 0 : b.quarantine ? -1 : 1,
  change: (a: CountryData, b: CountryData) => {
    if (!isFinite(+a.change) && !isFinite(+b.change)) {
      return 0
    }
    if (!isFinite(+a.change)) {
      return 1
    }
    if (!isFinite(+b.change)) {
      return -1
    }
    return +a.change - +b.change
  },
  change_reverse: (a: CountryData, b: CountryData) => {
    if (!isFinite(+b.change) && !isFinite(+a.change)) {
      return 0
    }
    if (!isFinite(+a.change)) {
      return 1
    }
    if (!isFinite(+b.change)) {
      return -1
    }
    return +b.change - +a.change
  },

  sum14: (a: CountryData, b: CountryData) => (b.sum14 > a.sum14 ? 1 : -1),
  sum14_reverse: (a: CountryData, b: CountryData) =>
    b.sum14 > a.sum14 ? -1 : 1,
  name: (a: CountryData, b: CountryData) => a.name.localeCompare(b.name),
  name_reverse: (a: CountryData, b: CountryData) => b.name.localeCompare(a.name)
}

export function AppComponent() {
  const [countryData, setCountryData] = useState<CountryData[]>()

  const size = useWindowSize()
  const [showAll, setShowAll] = useState<boolean>(false)
  const [filterfor, setFilter] = useState<string | undefined>()
  const [sortType, setSort] = useState<string>('perPop')
  const [updated, setUpdated] = useState<string>('2021-11-30')

  useEffect(() => {
    Promise.all([
      fetch(
        'https://storage.googleapis.com/ta-interaktiv/corona-dashboard/countries-quarantine.json'
      ).then(response => {
        return response.json()
      }),
      fetch(
        'https://storage.googleapis.com/ta-interaktiv/corona-dashboard/bag_covid_19_data_csv/COVID19IntQua.csv'
      ).then(response => {
        return response.text()
      })
    ]).then(results => {
      let countries = results[0].filter(
        f => countriesToRemove.indexOf(f.code) === -1
      )
      let BAGQuarantineData = csvParse(results[1]).filter(
        f =>
          f.geoLevel === 'ISO2' &&
          f.quarantineStart !== 'NA' &&
          (new Date() <= new Date(f.quarantineEnd as string) ||
            f.quarantineEnd === 'NA')
      )
      setUpdated(
        (BAGQuarantineData[0]
          ? BAGQuarantineData[0].version
          : countries[0].date
        ).split('_')[0]
      )

      countries = countries.map(m => {
        let el = BAGQuarantineData.find(f => f.geoRegion === m.code)
        if (el) {
          m.quarantine = true
          m.qstart = el.quarantineStart
          m.qend = el.quarantineEnd
        } else {
          m.quarantine = false
        }
        return m
      })
      setCountryData(countries)
    })
  }, [])

  const countryfilter = f => {
    if (!filterfor) return f
    if (filterfor !== undefined) {
      let name = LOCALES.lang === 'fr' && f.namefr ? f.namefr : f.name
      if (name.toLocaleLowerCase().includes(filterfor.toLocaleLowerCase())) {
        return f
      }
    }
  }
  return (
    <div className='index'>
      <ThemeSwitch />
      <div className='title'>{LOCALES.title}</div>
      <div className='subline'>{LOCALES.sub} </div>

      <div>
        <input
          placeholder={LOCALES.search.placeholder}
          type='search'
          value={filterfor || ''}
          onChange={e => {
            setFilter(e.target.value)
          }}
          onFocus={e => e.stopPropagation()}
          onClick={e => e.stopPropagation()}
        />
      </div>

      <div className='tableHead'>
        {[
          { type: 'name', label: LOCALES.tablehead.country, align: 'left' },
          !size.isMobile
            ? {
                type: 'quar',
                label: LOCALES.tablehead.quarantine,
                align: 'left'
              }
            : undefined,
          { type: 'sum14', label: LOCALES.tablehead._14d, align: 'right' },

          {
            type: 'perPop',
            label: size.isMobile
              ? LOCALES.tablehead.pop_short
              : LOCALES.tablehead.pop_long,
            align: 'right'
          },
          size.isMobile
            ? {
                type: 'quar',
                label: LOCALES.tablehead.quarantine,
                align: 'left'
              }
            : undefined,
          {
            type: 'change',
            label: LOCALES.tablehead.previousWeek,
            align: 'right'
          }
        ].map(m => {
          if (!m) return null
          return (
            <div
              className={m.type + ' ' + m.align}
              onClick={() =>
                setSort(sortType === m.type ? m.type + '_reverse' : m.type)
              }
            >
              {sortType.includes(m.type) && (
                <i
                  className={
                    'caret ' + (sortType === m.type ? 'down' : 'up') + ' icon'
                  }
                />
              )}
              {m.label}
            </div>
          )
        })}
      </div>

      <>
        {countryData && countryData.filter(countryfilter).length === 0 && (
          <div className='notfound'>{LOCALES.search.noResult}</div>
        )}
        {countryData &&
          countryData
            .filter(countryfilter)
            .sort(sorts[sortType])
            .map((m, i) => {
              if (i > maxperstart + 1 && !showAll) return null

              return (
                <>
                  {(i <= maxperstart || showAll) && (
                    <CountryRow el={m} LOCALES={LOCALES} />
                  )}
                </>
              )
            })}
      </>
      <button className='showmore' onClick={() => setShowAll(!showAll)}>
        {!showAll ? LOCALES.moreButton.showAll : LOCALES.moreButton.showLess}
      </button>
      <div className='source'>
        {LOCALES.footer}
        {formatDate(updated)}
      </div>
    </div>
  )
}
